import {
  CheckCircle2,
  CircleAlert,
  CircleDashed,
  CircleHelp,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { CHECK_STAGE_LABEL, type CheckStage, type PreflightKey } from '@hcc/types';
import { Pill, type PillTone } from '../ui/Pill';

/**
 * The inductee-facing Pre-Employment Checklist on /me. Each row shows
 * the stored gate status, except where the person holds a screening
 * check task (DBS / DVLA): then the row reflects the live workflow
 * stage (Pending application -> Pending HR Review -> Awaiting Physical
 * Check -> Complete) so they always know whose court the ball is in.
 */

export type PreflightStatus = 'ok' | 'pending' | 'missing' | 'na';

export interface Preflight {
  dbs_status: PreflightStatus;
  right_to_work: PreflightStatus;
  dvla_check: PreflightStatus;
  p45_received: PreflightStatus;
  notes: string | null;
  updated_at: string | null;
}

/** The slice of a me-view task this card needs to find check stages. */
export interface PreflightCheckTask {
  onboarding_kind?: string;
  preflight_key?: PreflightKey;
  check_stage?: CheckStage;
}

interface StyleEntry {
  label: string;
  icon: LucideIcon;
  tone: PillTone;
  /** Extra line under the row label, when the stage needs explaining. */
  hint?: string;
}

const PRE_STATUS_STYLE: Record<PreflightStatus, StyleEntry> = {
  ok: { label: 'OK', icon: CheckCircle2, tone: 'success' },
  pending: { label: 'Pending', icon: CircleDashed, tone: 'warning' },
  missing: { label: 'Missing', icon: CircleAlert, tone: 'danger' },
  na: { label: 'N/A', icon: CircleHelp, tone: 'neutral' },
};

const STAGE_STYLE: Record<CheckStage, StyleEntry> = {
  not_applied: {
    label: CHECK_STAGE_LABEL.not_applied,
    icon: CircleDashed,
    tone: 'warning',
    hint: 'Follow the instructions in your onboarding list above.',
  },
  applied: {
    label: CHECK_STAGE_LABEL.applied,
    icon: CircleDashed,
    tone: 'info',
  },
  processed: {
    label: CHECK_STAGE_LABEL.processed,
    icon: ShieldCheck,
    tone: 'warning',
    hint: 'Bring the ORIGINAL certificate on your start day; copies cannot be accepted.',
  },
  verified: { label: 'OK', icon: CheckCircle2, tone: 'success' },
  changes_requested: {
    label: CHECK_STAGE_LABEL.changes_requested,
    icon: CircleAlert,
    tone: 'danger',
    hint: 'See the note on your onboarding list above.',
  },
};

const ROWS: Array<{ key: keyof Preflight & string; gate: PreflightKey | null; label: string }> = [
  { key: 'dbs_status', gate: 'dbs', label: 'DBS check' },
  { key: 'right_to_work', gate: 'right_to_work', label: 'Right to work' },
  { key: 'dvla_check', gate: 'dvla', label: 'DVLA check' },
  { key: 'p45_received', gate: 'p45', label: 'P45 or HMRC Starter Checklist' },
];

export function PreflightCard({
  preflight,
  tasks = [],
}: {
  preflight: Preflight;
  tasks?: PreflightCheckTask[];
}) {
  function styleFor(row: (typeof ROWS)[number]): StyleEntry {
    // A live check task wins over the stored enum; the stored value only
    // turns 'ok' at the very end anyway (on the physical verification).
    const check = row.gate
      ? tasks.find(
          (t) => t.onboarding_kind === 'check' && t.preflight_key === row.gate && t.check_stage,
        )
      : undefined;
    if (check?.check_stage) return STAGE_STYLE[check.check_stage];
    return PRE_STATUS_STYLE[preflight[row.key] as PreflightStatus];
  }

  const anyMissing = ROWS.some((r) => preflight[r.key] === 'missing');
  return (
    <section className="overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
      <div className="flex items-center gap-2 border-b border-divider/10 px-5 py-3">
        <ShieldCheck className="h-4 w-4 text-accent" />
        <h2 className="text-sm font-semibold text-fg">Pre-Employment Checklist</h2>
      </div>
      {anyMissing ? (
        <p className="border-b border-danger/20 bg-danger-soft/15 px-5 py-2 text-xs text-danger-soft-fg">
          If anything below is missing, please bring it to reception.
        </p>
      ) : null}
      <ul className="divide-y divide-divider/10">
        {ROWS.map((row) => {
          const s = styleFor(row);
          const Icon = s.icon;
          return (
            <li key={row.key} className="px-5 py-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-fg">{row.label}</span>
                <Pill tone={s.tone} icon={<Icon className="h-3 w-3" />}>
                  {s.label}
                </Pill>
              </div>
              {s.hint ? <p className="mt-1 text-xs text-fg-muted">{s.hint}</p> : null}
            </li>
          );
        })}
      </ul>
      {preflight.notes ? (
        <div className="border-t border-divider/10 bg-surface-2 px-5 py-2 text-xs text-fg-muted">
          <span className="font-semibold">Notes from HR: </span>
          {preflight.notes}
        </div>
      ) : null}
    </section>
  );
}
